import type { Theme, ThemeOptions } from '@mui/material/styles'
import { createTheme } from '@mui/material'
import { deepPurple, lightBlue, pink } from '@mui/material/colors'
import { darkGrey } from '@/core/theme/colors.ts'
import { sharedButtonStyles } from '@/core/theme/style-variants.ts'

declare module '@mui/material/styles' {
  interface Palette {
    dividerHighContrast: string
    paperElevation1: string
  }

  interface PaletteOptions {
    dividerHighContrast?: string
    paperElevation1?: string
  }
}

const darkComponents: ThemeOptions['components'] = {
  MuiPaper: {
    styleOverrides: {
      root: { backgroundImage: 'none' },
    },
  },
}

const mobilePopoverFix: ThemeOptions['components'] = {
  MuiPopover: {
    defaultProps: {
      disableScrollLock: true,
      disableAutoFocus: true,
    },
  },
  MuiModal: {
    defaultProps: {
      disableScrollLock: true,
    },
  },
  MuiSelect: {
    styleOverrides: {
      // Hidden native <select> gets focused on open; iOS Safari scrolls to it.
      // position:fixed keeps it in viewport coords so no scroll occurs.
      nativeInput: { position: 'fixed' },
    },
  },
}

const sharedComponents: ThemeOptions['components'] = {
  MuiIconButton: {
    styleOverrides: { root: { ...sharedButtonStyles, padding: 4 } },
  },
  MuiButton: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...sharedButtonStyles,
        ...(theme.palette.mode === 'dark' && {
          backgroundColor: theme.palette.background.default,
        }),
        border: `1px ${theme.palette.dividerHighContrast} solid`,
        '&:hover': {
          borderColor: theme.palette.primary.main,
        },
      }),
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        '&:focus, &.Mui-focusVisible': {
          outline: 'none',
        },
      },
    },
  },
}

export const lightTheme: Theme = createTheme({
  cssVariables: true,
  components: { ...sharedComponents, ...mobilePopoverFix },
  palette: {
    mode: 'light',
    primary: lightBlue,
    secondary: deepPurple,
    divider: '#CECECE',
    dividerHighContrast: '#E8E8E8',
  },
})

export const darkTheme: Theme = createTheme({
  cssVariables: true,
  components: { ...sharedComponents, ...darkComponents, ...mobilePopoverFix },
  palette: {
    mode: 'dark',
    primary: pink,
    secondary: {
      light: '#f1ee75',
      main: '#ebbd29',
      dark: '#e57e07',
    },
    text: {
      primary: '#EEEEEE',
    },
    background: {
      paper: darkGrey[150],
      default: darkGrey[50],
    },
    divider: darkGrey[250],
    dividerHighContrast: darkGrey[600],
    paperElevation1: darkGrey[300],
  },
})
